import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Typography,
  Grid,
  Box,
  Avatar,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  OutlinedInput,
  Input,
  TextField,
} from "@mui/material";
import {
  useAdminGetJobByIdQuery,
  useAdminUpdateJobAssignmentMutation,
  useAdminUpdateJobMutation,
  useLazyAdminGetCompaniesQuery,
  useLazyAdminGetCompanyEmployeesQuery,
} from "@diditusa/shared/api/adminApi";
import { format } from "date-fns";
import Person from "@mui/icons-material/Person";
import { Company } from "@diditusa/shared/types/companyTypes";
import { JobStatus } from "@diditusa/shared/types/jobTypes";

const JobDetails = () => {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const {
    data: job,
    isLoading,
    error,
  } = useAdminGetJobByIdQuery(parseInt(jobId!));

  const [
    getAllCompaniesTrigger,
    { data: companies, ...getAllCompaniesState },
    _companiesLastPromisInfo,
  ] = useLazyAdminGetCompaniesQuery();

  const [
    getCompanyEmployeesTrigger,
    { data: employees, ...getEmployeesState },
    _employeeLastPromisInfo,
  ] = useLazyAdminGetCompanyEmployeesQuery();

  const [updateJob, updateJobState] = useAdminUpdateJobMutation();

  const [updateJobAssignments, updateJobAssignmentsState] =
    useAdminUpdateJobAssignmentMutation();

  const [showCompanySelect, _setShowCompanySelect] = useState(false);
  const [selectedCompany, _setSelectedCompany] = useState<number | undefined>(
    job?.companyId
  );

  const [showEmployeeSelect, _setShowEmployeeSelect] = useState(false);
  const [selectedEmployees, _setSelectedEmployees] = useState<string[]>([]);
  const [dirty, setDirty] = useState(false);
  const [jobNotesDirty, setJobNotesDirty] = useState(false);
  const [jobNotes, setJobNotes] = useState<string | undefined>();

  useEffect(() => {
    if (job) {
      setJobNotes(job.adminNotes);
    }
  }, [job]);

  useEffect(() => {
    if (showCompanySelect) {
      // Simulated API call
      fetchCompanies();
    }
  }, [showCompanySelect]);

  useEffect(() => {
    if (showEmployeeSelect && (selectedCompany || job?.company)) {
      fetchGuards();
    }
  }, [showEmployeeSelect, selectedCompany]);

  const setSelectedEmployees = (newEmployees: string[]) => {
    setDirty(true);
    _setSelectedEmployees(newEmployees);
  };

  const setSelectedCompany = (newCompany: number) => {
    setDirty(true);
    _setSelectedCompany(newCompany);
    if (showEmployeeSelect) {
      getCompanyEmployeesTrigger(newCompany);
    }
  };

  const setShowCompanySelect = (show: boolean) => {
    if (!show && !showEmployeeSelect) {
      setDirty(false);
    }
    _setShowCompanySelect(show);
  };

  const setShowEmployeeSelect = (show: boolean) => {
    if (!show && !showCompanySelect) {
      setDirty(false);
    }
    _setShowEmployeeSelect(show);
  };

  const updateJobNotes = (value: string) => {
    setJobNotesDirty(true);
    setJobNotes(value);
  };

  const fetchGuards = async () => {
    let id = selectedCompany;
    if (id === undefined) {
      id = job?.companyId;
    }
    if (id === undefined) {
      return;
    }
    getCompanyEmployeesTrigger(id);
  };

  const fetchCompanies = async () => {
    getAllCompaniesTrigger();
  };

  const saveChanges = async () => {
    if (!job) {
      return;
    }

    const id = job.id;

    if (
      (selectedCompany && selectedCompany !== job.companyId) ||
      jobNotesDirty
    ) {
      const companyId = selectedCompany || job.companyId;
      const adminNotes = jobNotes;
      await updateJob({ jobId: id, body: { companyId, adminNotes } });
      if (updateJobState.isError) {
        return;
      }
      setShowCompanySelect(false);
      setJobNotesDirty(false);
    }

    // todo: maybe figure out how to only make this call if the
    // employees changed.
    if (showEmployeeSelect) {
      await updateJobAssignments({
        jobId: id,
        body: { employeeIds: selectedEmployees },
      });
      if (updateJobAssignmentsState.isError) {
        return;
      }
      setShowEmployeeSelect(false);
    }

    setDirty(false);
  };

  const getEmployeeMenuItems = () => {
    if (getEmployeesState.isLoading) {
      return <MenuItem disabled={true}>Loading...</MenuItem>;
    }

    if (getEmployeesState.isError) {
      return <MenuItem disabled={true}>Error loading employees</MenuItem>;
    }

    if (!employees || employees.length === 0) {
      return <MenuItem disabled={true}>No employees found</MenuItem>;
    }

    return employees.map((employee) => (
      <MenuItem key={employee.id} value={employee.id}>
        {`${employee.firstName} ${employee.lastName}`}
      </MenuItem>
    ));
  };

  const getCompanyMenuItems = () => {
    if (getAllCompaniesState.isLoading) {
      return <MenuItem disabled={true}>Loading...</MenuItem>;
    }

    if (getAllCompaniesState.isError) {
      return <MenuItem disabled={true}>Error loading companies</MenuItem>;
    }

    if (!companies || companies.length === 0) {
      return <MenuItem disabled={true}>No companies found</MenuItem>;
    }

    return companies.map((company: Company) => (
      <MenuItem key={company.id} value={company.id}>
        {company.name}
      </MenuItem>
    ));
  };

  const onUserClick = () => {
    if (!job) {
      return;
    }
    navigate(`/users/${job?.createdById}`);
  };

  const onMarkJobCompleteClick = () => {
    if (!jobId) {
      return;
    }
    updateJob({
      jobId: parseInt(jobId),
      body: { status: JobStatus.COMPLETED },
    });
  };

  if (isLoading) {
    return <p>Loading job details...</p>;
  }

  if (error) {
    return <p>Error: {(error as any).data.message}</p>;
  }

  if (!job) {
    return <p>Job not found.</p>;
  }

  return (
    <Box component="main" sx={{ flexGrow: 1, textAlign: "left" }}>
      <Box sx={{ p: 4 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
          <Typography variant="h4" fontWeight="bold">
            Job Details: {job.reservationCode}
          </Typography>
        </Box>
        <Box
          display="flex"
          alignItems="center"
          gap={2}
          mb={4}
          onClick={onUserClick}
        >
          {job.createdBy?.imageUrl ? (
            <Avatar
              src={job.createdBy.imageUrl}
              sx={{ width: 96, height: 96, ":hover": { cursor: "pointer" } }}
            />
          ) : (
            <Person
              sx={{ width: 96, height: 96, ":hover": { cursor: "pointer" } }}
            />
          )}
          <Box>
            <Typography variant="h6" fontWeight={700}>
              {job.createdBy
                ? `${job.createdBy.firstName} ${job.createdBy.lastName}`
                : job.createdById}
            </Typography>
            <Typography color="text.secondary">
              {job.createdBy ? job.createdBy.email : ""}
            </Typography>
          </Box>
        </Box>
        {(job.status === JobStatus.AWAITING_COMPLETION_CONFIRMATION ||
          job.status === JobStatus.IN_PROGRESS) && (
          <Box>
            <Button variant="contained" onClick={onMarkJobCompleteClick}>
              Mark Job Completed
            </Button>
          </Box>
        )}

        <Section title="Description">
          <Typography>{job.description}</Typography>
        </Section>

        <Section title="Job Details">
          <DetailRow
            label="Job type"
            value={
              job.securityOption
                ? job.securityOption.title
                : `${job.securityOptionId}`
            }
          />
          <DetailRow label="Location" value={job.location} />
          <DetailRow
            label="Date"
            value={job.startTime.toFormat("EEEE, MM/dd/yyyy")}
          />
          <DetailRow
            label="Start time"
            value={job.startTime.toFormat("HH:mm a ZZZZ")}
          />
          <DetailRow label="Status" value={job.status.toString()} />
        </Section>

        <Section title="Admin Notes">
          <TextField
            value={jobNotes}
            onChange={(e) => updateJobNotes(e.currentTarget.value)}
            placeholder={"pal"}
            multiline
            rows={8}
            maxRows={8} // prevents cursor from moving off screen via new lines
            fullWidth
          />
          {jobNotesDirty && <Box></Box>}
        </Section>

        <Grid container sx={{ display: "flex" }}>
          <Grid size={6} sx={{ xs: 12 }}>
            <Section title="Assigned Company">
              <Typography>
                {job.company ? job.company.name : "Unassigned"}
              </Typography>
              {showCompanySelect && (
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel id="company-select-label">
                    Select Company
                  </InputLabel>
                  <Select
                    labelId="company-select-label"
                    value={selectedCompany}
                    label="Select Company"
                    sx={{ mr: 4 }}
                    onChange={(e) =>
                      setSelectedCompany(e.target.value as number)
                    }
                  >
                    {getCompanyMenuItems()}
                  </Select>
                </FormControl>
              )}
              {updateJobState.isError && (
                <Typography>{`Error updating company ${
                  (updateJobState.error as any).data.message
                }`}</Typography>
              )}
              <Button
                variant="outlined"
                onClick={() => setShowCompanySelect(!showCompanySelect)}
              >
                {showCompanySelect
                  ? "Cancel"
                  : job.company
                  ? "Change"
                  : "Assign to Company"}
              </Button>
            </Section>
          </Grid>
          <Grid size={6} sx={{ xs: 12 }}>
            <Section title="Assigned Guards">
              {job.jobAssignments && job.jobAssignments.length > 0 ? (
                job.jobAssignments.map((ja) => (
                  <Typography>
                    {ja.user
                      ? `${ja.user.firstName} ${ja.user.lastName}`
                      : ja.userId}
                  </Typography>
                ))
              ) : (
                <Typography>Unassigned</Typography>
              )}
              {showEmployeeSelect && (
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel id="guard-select-label">Select Guards</InputLabel>
                  <Select
                    sx={{ mr: 4 }}
                    labelId="guard-select-label"
                    multiple
                    value={selectedEmployees}
                    onChange={(e) =>
                      setSelectedEmployees(e.target.value as string[])
                    }
                    input={<OutlinedInput label="Select Guards" />}
                    renderValue={(selected) => {
                      if (!employees) {
                        return;
                      }
                      return (
                        <Box
                          sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}
                        >
                          {selected.map((id) => (
                            <Chip
                              key={id}
                              label={
                                employees.find((g) => g.id === id)?.firstName ||
                                id
                              }
                            />
                          ))}
                        </Box>
                      );
                    }}
                  >
                    {getEmployeeMenuItems()}
                  </Select>
                </FormControl>
              )}
              {updateJobAssignmentsState.isError && (
                <Typography>{`Error updating assigned agents ${
                  (updateJobAssignmentsState.error as any).data.message
                }`}</Typography>
              )}

              <Button
                variant="outlined"
                disabled={!selectedCompany && !job.company}
                onClick={() => setShowEmployeeSelect(!showEmployeeSelect)}
              >
                {showEmployeeSelect
                  ? "Cancel"
                  : job.jobAssignments
                  ? "Change"
                  : "Assign to Agent"}
              </Button>
            </Section>
          </Grid>
        </Grid>
        {(dirty || jobNotesDirty) && (
          <Box>
            <Button variant="contained" color="primary" onClick={saveChanges}>
              Save changes
            </Button>
          </Box>
        )}
      </Box>
    </Box>
  );
};

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({
  title,
  children,
}) => (
  <Box mb={4}>
    <Typography variant="h6" fontWeight={700} gutterBottom>
      {title}
    </Typography>
    {children}
  </Box>
);

const DetailRow: React.FC<{ label: string; value: string }> = ({
  label,
  value,
}) => (
  <Grid container spacing={2} sx={{ mb: 1 }}>
    <Grid size={6} sx={{ xs: 6 }}>
      <Typography color="text.secondary">{label}</Typography>
    </Grid>
    <Grid sx={{ xs: 6 }} size={6}>
      <Typography>{value}</Typography>
    </Grid>
  </Grid>
);

export default JobDetails;
